/**
 * ProofCaptureSheet — chụp hoặc chọn ảnh bằng chứng rồi gửi vào đơn.
 *
 * CEO chốt 17/08/2026: cho CẢ hai đường — bấm chụp trực tiếp (mở camera sau
 * trên điện thoại) hoặc lấy ảnh có sẵn trong máy. Ảnh upload xong mới ghi vào
 * sổ; người chụp và thời điểm do server đóng dấu, không nhận từ máy khách.
 */
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Textarea } from "@/ui/textarea";
import { PhotoThumbList, PhotoThumbRemove } from "@/components/PhotoThumb";
import { useAddProof } from "./api";
import { MAX_PROOF_IMAGES, type ProofKind } from "./utils";

const KIND_TITLE: Record<ProofKind, string> = {
  // Đổi tên 07/10/2026 (CEO chốt 3a) — cùng tên với khối trên chi tiết đơn.
  delivery: "Ảnh chụp cùng khách",
  makeup_before: "Ảnh TRƯỚC trang điểm",
  makeup_after: "Ảnh SAU trang điểm",
};

const KIND_HINT: Record<ProofKind, string> = {
  delivery: "Chụp ảnh check-in cùng khách và ảnh bàn giao sản phẩm in lúc trao cho khách.",
  makeup_before: "Chụp mặt mộc của khách trước khi bắt đầu trang điểm.",
  makeup_after: "Chụp lại sau khi trang điểm xong, cùng góc với ảnh trước.",
};

/** Ảnh đang chờ gửi: giữ luôn object URL để xem trước, thu hồi khi bỏ ảnh. */
type Pending = { file: File; url: string };

interface Props {
  orderId: string;
  kind: ProofKind;
  /** Ảnh trước/sau của mặt thứ mấy (chấm QC bản 3). Vắng = Mặt 1. */
  face?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chạy sau khi gửi thành công — vd: đi tiếp bước xác nhận đã giao. */
  onDone?: () => void;
  /** Hiện ô ghi chú (dùng cho ảnh giao: ghi số lượng đã trao). */
  withNote?: boolean;
}

/**
 * useProofPicker — giữ danh sách ảnh đang chờ gửi cùng object URL xem trước,
 * và thu hồi URL ngay lúc bỏ ảnh / đóng tấm để không rò bộ nhớ khi nhân viên
 * chụp đi chụp lại nhiều lần.
 */
function useProofPicker() {
  const [pending, setPending] = useState<Pending[]>([]);

  const pick = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = Array.from(list).map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPending((prev) => {
      const merged = [...prev, ...picked];
      merged.slice(MAX_PROOF_IMAGES).forEach((p) => URL.revokeObjectURL(p.url));
      return merged.slice(0, MAX_PROOF_IMAGES);
    });
  };

  const drop = (index: number) =>
    setPending((prev) => {
      const gone = prev[index];
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((_, i) => i !== index);
    });

  const clearAll = useCallback(() => {
    setPending((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
  }, []);

  return { pending, pick, drop, clearAll };
}

export function ProofCaptureSheet({
  orderId,
  kind,
  face,
  open,
  onOpenChange,
  onDone,
  withNote,
}: Props) {
  const { pending, pick, drop, clearAll } = useProofPicker();
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const addM = useAddProof();
  const sending = addM.isPending;

  // Đang gửi thì KHÔNG cho đóng hộp (log prod 29–30/09/2026): đóng giữa chừng
  // thì lượt gửi vẫn chạy ngầm và vẫn lưu, người dùng tưởng hỏng nên gửi lại —
  // một đơn có tới 3 bản cùng một tấm.
  const close = (next: boolean) => {
    if (!next && sending) return;
    if (!next) clearAll();
    onOpenChange(next);
  };

  const submit = () => {
    if (pending.length === 0) {
      toast.error("Chưa có ảnh nào — chụp hoặc chọn ít nhất 1 ảnh");
      return;
    }
    addM.mutate(
      {
        orderId,
        kind,
        ...(face ? { face } : {}),
        files: pending.map((p) => p.file),
        ...(note.trim() ? { note: note.trim() } : {}),
        onProgress: (current, total) => setProgress({ current, total }),
      },
      {
        onSuccess: () => {
          toast.success("Đã lưu bằng chứng");
          clearAll();
          setNote("");
          onOpenChange(false);
          onDone?.();
        },
        onError: (err: unknown) =>
          toast.error(err instanceof Error ? err.message : "Gửi bằng chứng thất bại"),
        onSettled: () => setProgress(null),
      },
    );
  };

  const sendLabel = !sending
    ? `Gửi ${pending.length} ảnh`
    : progress && progress.total > 1
      ? `Đang gửi ảnh ${progress.current}/${progress.total}…`
      : "Đang gửi ảnh…";

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        hideCloseButton={sending}
        onEscapeKeyDown={(e) => {
          if (sending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (sending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {KIND_TITLE[kind]}
            {face && face > 1 ? ` · Mặt ${face}` : ""}
          </DialogTitle>
          <DialogDescription>{KIND_HINT[kind]}</DialogDescription>
        </DialogHeader>

        <PickButtons onPick={pick} disabled={sending} />

        <PendingGrid items={pending} onDrop={drop} disabled={sending} />

        {withNote ? (
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ghi chú số lượng đã giao (vd: 6 ảnh 4x6 + 2 ảnh 6x9)"
            rows={2}
          />
        ) : null}

        {sending ? (
          <p role="status" className="text-xs text-muted-foreground">
            Đang gửi — giữ màn này tới khi xong, đừng tắt hay gửi lại.
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" disabled={sending} onClick={submit}>
            {sendLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Hai đường lấy ảnh — chụp thẳng hoặc chọn có sẵn (CEO chốt 17/08/2026). */
function PickButtons({
  onPick,
  disabled,
}: {
  onPick: (list: FileList | null) => void;
  disabled: boolean;
}) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPick(e.target.files);
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap gap-2">
      {/* capture="environment" mở thẳng camera sau trên điện thoại */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid="proof-camera-input"
        onChange={onChange}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="proof-gallery-input"
        onChange={onChange}
      />
      <Button type="button" disabled={disabled} onClick={() => cameraRef.current?.click()}>
        Chụp ảnh
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => galleryRef.current?.click()}
      >
        Chọn ảnh có sẵn
      </Button>
    </div>
  );
}

function PendingGrid({
  items,
  onDrop,
  disabled,
}: {
  items: Pending[];
  onDrop: (index: number) => void;
  disabled: boolean;
}) {
  // Khung ảnh chuẩn 8x10, bấm xem lớn (CEO chốt 27/09/2026) — soát ảnh mờ,
  // lệch khung TRƯỚC khi gửi, vì gửi rồi là thành bằng chứng của đơn.
  return (
    <PhotoThumbList
      images={items.map((p) => p.url)}
      label="Ảnh vừa chọn"
      alt={(i) => `Ảnh ${i + 1} chờ gửi`}
      openLabel={(i) => `Xem lớn ảnh vừa chọn ${i + 1}`}
      overlay={(_, i) => (
        <PhotoThumbRemove label={`Bỏ ảnh ${i + 1}`} disabled={disabled} onClick={() => onDrop(i)} />
      )}
    />
  );
}
